// frontend/src/views/Invitations.jsx

import React, { useEffect, useState } from 'react'
import moment from 'moment';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

// Lógica de sesión
import { getStoredSession, setStoredSession, clearStoredSession } from '../hooks/useHashSession'
import { useGroupInfo } from '../hooks/useGroupInfo'

// Servicios
import { getInvitations, updateInvitationStatus, deleteInvitation } from '../services/invitationService'

// Componentes visuales
import GroupInfoEditor from '../components/GroupInfoEditor'
import InvitationCreator from '../components/InvitationCreator'
import InvitationDeleteModal from '../components/InvitationDeleteModal'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import GroupHeader from '../components/GroupHeader'

// Función de traducción
import { useTranslation } from '../i18n'

// Funciones utilitarias
import { formatDateForDisplay } from '../utils/dateUtils'


/**
 * Vista para líderes de grupo: gestión de invitaciones activas
 */
export default function Invitations() {
  const [session, setSession] = useState(getStoredSession());
  const [invitations, setInvitations] = useState([]);
  const [groupInfo, setGroupInfo] = useState(null);
  const [error, setError] = useState('');
  const [okMsg, setOkMsg] = useState('');
  const [totalMembers, setTotalMembers] = useState(0);
  const [showEditor, setShowEditor] = useState(false);
  const [showCreator, setShowCreator] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [loading, setLoading] = useState(false);

  // --- Estados de paginación ---
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(null);
  const [total, setTotal] = useState(0);
  const totalPages = Math.max(1, Math.ceil(total / Math.max(Number(limit) || 1, 1)));

  // Estado local para los flags
  const [notifyFlags, setNotifyFlags] = useState({
    notify_by_email: false,
    notify_by_whatsapp: false,
    cc_leader: false
  });

  // helper para mostrar y auto-ocultar el mensaje
  const flashOk = (msg) => {
    setOkMsg(msg);
    // opcional: auto-clear a los 4s
    setTimeout(() => setOkMsg(''), 4000);
  };

  // Diálogo de reenvío
  const [resendOpen, setResendOpen] = useState(false);
  const [resendTarget, setResendTarget] = useState(null);
  const [ccLeader, setCcLeader] = useState(false);
  const [resending, setResending] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { _ } = useTranslation();
  const language = localStorage.getItem('lang') || 'en';
  const navigationGroupState = location.state?.groupState || null;

  // Validación temprana
  if (!session || session.role !== 'leader') {
    return <Navigate to="/" replace />;
  }

  useEffect(() => {
    setError('');
    setOkMsg('');

    setGroupInfo(prev => ({
      group_name: prev?.group_name || session?.groupName || null,
      owner_name: prev?.owner_name || session?.ownerName || null,
      state: prev?.state || session?.state || navigationGroupState || 'active',
    }));

    // Cargar invitaciones con paginación
    setLoading(true);
    getInvitations(session.hash, page, limit) // si limit===null no se envía
      .then((data) => {
        setInvitations(data.invitations);
        setTotalMembers(data.total_members);

        // Meta del backend
        const backendTotal = data?.meta?.total ?? 0;
        const backendPage = data?.meta?.page ?? 1;
        const backendLimit = Number(data?.meta?.limit ?? 0) || null;

        setTotal(backendTotal);

        // Solo ajustar page si el backend difiere (evita loop)
        if (backendPage !== page) setPage(backendPage);

        // Solo fijar limit una vez (cuando aún es null)
        if ((limit === null || limit === undefined) && backendLimit != null) {
          setLimit(backendLimit);
        }
      })
      .catch((err) => {
        setError(err?.error || _('Error loading invitations'));
      })
      .finally(() => setLoading(false));
  }, [session.hash, page, limit]); // usa session.hash, no todo el objeto session

  const handleBlock = async (invHash) => {
    try {
      await updateInvitationStatus(invHash, 'blocked', session.hash)
      setInvitations(prev =>
        prev.map(inv => inv.hash === invHash ? { ...inv, status: 'blocked' } : inv)
      )
    } catch {
      alert(_('Error blocking invitation'))
    }
  }
  
  const handleCloseGroup = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE}/api/group/state`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-access-hash': session.hash
        },
        body: JSON.stringify({ state: 'closed' })
      });

      if (!res.ok) throw new Error('Request failed');

      const updated = await res.json();
      setGroupInfo(prev => ({
        ...prev,
        state: updated?.state || 'closed'
      }));
    } catch {
      alert(_('Error closing group'));
    }
  };

  const handleDelete = async (invHash) => {
    try {
      await deleteInvitation(invHash, session.hash)
      setInvitations(prev => prev.filter(inv => inv.hash !== invHash))
      setDeleteTarget(null)
    } catch (err) {
      alert(err.message)
    }
  }

  const handleReactivate = async (invHash) => {
    try {
      await updateInvitationStatus(invHash, 'active', session.hash)
      setInvitations(prev =>
        prev.map(inv => inv.hash === invHash ? { ...inv, status: 'active' } : inv)
      )
    } catch {
      alert(_('Error reactivating invitation'))
    }
  }

  const handleReopenGroup = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE}/api/group/state`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-access-hash': session.hash
        },
        body: JSON.stringify({ state: 'active' })
      });

      if (!res.ok) throw new Error('Request failed');

      const updated = await res.json();
      setGroupInfo(prev => ({
        ...prev,
        state: updated?.state || 'active'
      }));
    } catch {
      alert(_('Error reopening group'));
    }
  };

  const handleSaveGroupInfo = (updated) => {
    setGroupInfo(prev => ({
      ...prev,
      ...updated
    }))
    setShowEditor(false)
  }

  const handleUnblock = async (invHash) => {
    try {
      await updateInvitationStatus(invHash, 'active', session.hash)
      setInvitations(prev =>
        prev.map(inv => inv.hash === invHash ? { ...inv, status: 'active' } : inv)
      )
    } catch {
      alert(_('Error unblocking invitation'))
    }
  }

  // Detecta si el grupo está cerrado
  const effectiveGroupInfo = useGroupInfo(groupInfo, session)
  const isClosed = effectiveGroupInfo?.state === 'closed';

  // Renderiza un aviso si el grupo está en modo solo lectura
  const closedBanner = isClosed && session.role === 'leader' ? (
    <div className="banner-closed">
      {_('This group is closed.')}
    </div>
  ) : null;

  // --- Reenvío de correo ---
  function openResendDialog(inv) {
    setError('');
    setOkMsg('');
    setResendTarget(inv);
    setCcLeader(false);
    setResendOpen(true);
  }

  async function handleResend() {
    if (!resendTarget) return;
    setResending(true);
    setError('');
    setOkMsg('');

    try {
      const resp = await fetch(
        `${process.env.REACT_APP_API_BASE}/api/invitation/${resendTarget.hash}/send-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-access-hash': session.hash
          },
          body: JSON.stringify({
            cc_leader: !!ccLeader,
            group_name: groupInfo?.group_name,
            owner_name: groupInfo?.owner_name,
            owner_email: groupInfo?.owner_email
          })
        }
      );

      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data?.error || _('Could not resend email.'));
      }

      setOkMsg(_('Invitation email sent successfully'));
      setResendOpen(false);
      setResendTarget(null);
    } catch (e) {
      setError(e.message || _('Could not resend email.'));
    } finally {
      setResending(false);
    }
  }

  // Funciones para manejar la paginación
  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const PaginationControls = () => (
    <div className="flex items-center justify-between my-4">
      <button
        onClick={() => handlePageChange(page - 1)}
        disabled={page === 1 || loading}
        className="button-secondary"
      >
        {_('Prev')}
      </button>
      <span className="text-sm">
        {_('Page')} {page} {_('of')} {totalPages}
      </span>
      <button
        onClick={() => handlePageChange(page + 1)}
        disabled={page === totalPages || loading}
        className="button-secondary"
      >
        {_('Next')}
      </button>
    </div>
  );

  return (
    <div className="container">
      {closedBanner}

       <GroupHeader
         _={_}
         session={session}
         effectiveGroupInfo={effectiveGroupInfo}
         isReadOnly={false}
         secondaryInfo={
           <p className="header-sub">
             {_('Total People')}: <span className="font-semibold">{totalMembers}</span>
           </p>
         }
         actions={
           session.role === 'leader' ? (
             <div className="flex gap-1 mt-2 md:mt-0">
               <button
                 className="icon-button text-emerald-600"
                 title={_('Export to Excel')}
                 onClick={async () => {
                   try {
                     const API = process.env.REACT_APP_API_BASE;
                     const res = await fetch(`${API}/api/group/export`, {
                       method: 'GET',
                       headers: { 'x-access-hash': session.hash }
                     });

                     if (!res.ok) throw new Error();

                     const blob = await res.blob();
                     const url = window.URL.createObjectURL(blob);

                    const disposition = res.headers.get('content-disposition') || '';
                    const match = disposition.match(/filename="([^"]+)"/i);
                    const filename = match?.[1] || 'goval-entry.xlsx';

                    const a = document.createElement('a');
                    a.href = url;
                    a.download = filename;
                     document.body.appendChild(a);
                     a.click();
                     a.remove();

                     window.URL.revokeObjectURL(url);
                   } catch (err) {
                     console.error('Export failed', err);
                   }
                 }}
               >
                 <FontAwesomeIcon icon="file-excel" />
               </button>
               {effectiveGroupInfo?.state !== 'closed' ? (
                 <>
                   <button
                     className="icon-button text-blue-600"
                     title={_('Edit group')}
                     onClick={() => setShowEditor(true)}
                   >
                     <FontAwesomeIcon icon="pencil-alt" />
                   </button>
              
                   <button
                     className="icon-button text-green-600"
                     title={_('Create invitation')}
                     onClick={() => setShowCreator(true)}
                   >
                     <FontAwesomeIcon icon="plus" />
                   </button>
              
                   <button
                     className="icon-button text-red-600"
                     title={_('Close group')}
                     onClick={handleCloseGroup}
                   >
                     <FontAwesomeIcon icon="lock" />
                   </button>
                 </>
               ) : (
                 <button
                   className="icon-button text-green-600"
                   title={_('Reopen group')}
                   onClick={handleReopenGroup}
                 >
                   <FontAwesomeIcon icon="lock-open" />
                 </button>
               )}
             </div>
           ) : null
         }
       />

      {error && <p className="error-text mb-2">{error}</p>}
      {okMsg && (
        <p className="text-sm text-emerald-600 mb-2">
          {okMsg}
        </p>
      )}

      <table className="table-default hidden md:table w-full">
        <thead>
          <tr className="table-head-row">
            <th>{_('Hash')}</th>
            <th>{_('Name')}</th>
            <th>{_('Phone')}</th>
            <th>{_('Email')}</th>
            <th>{_('Status')}</th>
            <th>{_('Created')}</th>
            <th className="!text-center">{_('Total Members')}</th>
            <th>{_('Actions')}</th>
          </tr>
        </thead>
        <tbody>

          {Array.isArray(invitations) && invitations.map(inv => {
            const status = inv.status || 'active'
            const isLeader = inv.role === 'leader' || status === 'internal'
            const textMutedClass = isLeader ? 'text-muted' : ''

          const canResend =
            !!inv.contact_email &&
            !isLeader &&
            effectiveGroupInfo?.state !== 'closed';

            return (
              <tr key={inv.hash} className="table-row">
                <td className={`text-xs whitespace-nowrap pr-4 ${textMutedClass}`}>{inv.hash}</td>
                <td className={textMutedClass}>{inv.contact_name || '-'}</td>
                <td className={textMutedClass}>{inv.contact_phone || '-'}</td>
                <td className={textMutedClass}>
                  {typeof inv.contact_email === 'string'
                    ? inv.contact_email.replace(/\s+/g, '')
                    : '-'}
                </td>
                <td className={`capitalize ${textMutedClass}`}>{_(status)}</td>
                <td className={textMutedClass}>
                  {moment(inv.created_at).format('DD/MM/YYYY hh:mm A')}
                </td>
                <td className={`text-center ${textMutedClass}`}>{inv.members_count}</td>
                <td className="flex flex-row gap-1 items-center">
                  <button
                    onClick={() => {
                      const isLeaderRow = inv.role === 'leader' || inv.status === 'internal'

                      // Siempre guardar el parentHash para saber que vinimos desde Invitations
                      setStoredSession(
                        inv.hash,
                        isLeaderRow ? 'leader' : 'guest',
                        session.hash,
                        effectiveGroupInfo?.group_name || null,
                        effectiveGroupInfo?.owner_name || null
                      )

                      navigate('/members', {
                        state: {
                          createdByHash: isLeaderRow ? inv.hash : null,
                          groupState: effectiveGroupInfo?.state || null
                        }
                      })
                    }}
                    className="icon-button"
                    title={_('View members')}
                  >
                    <FontAwesomeIcon icon="users" />
                  </button>

                  {canResend && (
                    <button
                      onClick={() => openResendDialog(inv)}
                      className="icon-button text-emerald-600"
                      title={_('Resend email')}
                      disabled={!canResend}
                    >
                      <FontAwesomeIcon icon="envelope" />
                    </button>
                  )}

                  {isLeader || effectiveGroupInfo?.state === 'closed' ? (
                    <span className="text-muted">{_('Group owner')}</span>
                  ) : status === 'active' ? (
                    <button
                      onClick={() => handleBlock(inv.hash)}
                      className="icon-button text-red-600"
                      title={_('Block')}
                    >
                      <FontAwesomeIcon icon="lock" />
                    </button>
                  ) : status === 'blocked' ? (
                    <button
                      onClick={() => handleUnblock(inv.hash)}
                      className="icon-button text-green-600"
                      title={_('Unblock')}
                    >
                      <FontAwesomeIcon icon="lock-open" />
                    </button>
                  ) : status === 'revoked' ? (
                    <button
                      onClick={() => handleReactivate(inv.hash)}
                      className="icon-button text-blue-600"
                      title={_('Reactivate')}
                    >
                      <FontAwesomeIcon icon="undo" />
                    </button>
                  ) : (
                    <span className="text-muted">{_('No actions')}</span>
                  )}

                {session.role === 'leader' &&
                  !isLeader &&
                  effectiveGroupInfo?.state !== 'closed' && (
                      <button
                        onClick={() => setDeleteTarget(inv.hash)}
                        className="icon-button text-red-500"
                        title={_('Delete')}
                      >
                        <FontAwesomeIcon icon="trash" />
                      </button>
                    )}
                </td>
              </tr>
            )
          })}

        </tbody>
      </table>


      {/* Vista para Móviles */}
      <div className="grid grid-cols-1 gap-4 md:hidden">
        {Array.isArray(invitations) && invitations.map(inv => {
          const status = inv.status || 'active'
          const isLeader = inv.role === 'leader' || status === 'internal'

            const canResend =
              !!inv.contact_email &&
              !isLeader &&
              effectiveGroupInfo?.state !== 'closed';

          return (
            <div key={inv.hash} className={`card ${isLeader ? 'opacity-75 text-muted' : ''}`}>
              <div className="card-list">
                <div className="grid grid-cols-3 gap-x-2">
                  <span className="col-span-1 label">{_('Name')}:</span>
                  <span className="col-span-2 font-semibold truncate">
                    {inv.contact_name || '-'}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-x-2">
                  <span className="col-span-1 label">{_('Phone')}:</span>
                  <span className="col-span-2">{inv.contact_phone || '-'}</span>
                </div>
                <div className="grid grid-cols-3 gap-x-2">
                  <span className="col-span-1 label">{_('Status')}:</span>
                  <span className="col-span-2 capitalize">{_(status)}</span>
                </div>
                <div className="grid grid-cols-3 gap-x-2">
                  <span className="col-span-1 label">{_('Members')}:</span>
                  <span className="col-span-2">{inv.members_count}</span>
                </div>
              </div>

              {/* Acciones */}
              <div className="flex flex-row gap-1 items-center border-t dark:border-gray-700 mt-3 pt-2">
                <button
                  onClick={() => {
                    const isLeaderRow = inv.role === 'leader' || inv.status === 'internal'
                    setStoredSession(
                      inv.hash,
                      isLeaderRow ? 'leader' : 'guest',
                      session.hash,
                      effectiveGroupInfo?.group_name || null,
                      effectiveGroupInfo?.owner_name || null
                    )
                    navigate('/members', {
                      state: {
                        createdByHash: isLeaderRow ? inv.hash : null,
                        groupState: groupInfo?.state || null
                      }
                    })
                  }}
                  className="icon-button"
                  title={_('View members')}
                >
                  <FontAwesomeIcon icon="users" />
                </button>

                {canResend && (
                  <button
                    className="icon-button text-emerald-600"
                    onClick={() => openResendDialog(inv)}
                    title={_('Resend email')}
                  >
                    <FontAwesomeIcon icon="envelope" />
                  </button>
                )}

                {isLeader || effectiveGroupInfo?.state === 'closed' ? (
                  <span className="text-muted text-xs">{_('Group owner')}</span>
                ) : status === 'active' ? (
                  <button
                    onClick={() => handleBlock(inv.hash)}
                    className="icon-button text-red-600"
                    title={_('Block')}
                  >
                    <FontAwesomeIcon icon="lock" />
                  </button>
                ) : status === 'blocked' ? (
                  <button
                    onClick={() => handleUnblock(inv.hash)}
                    className="icon-button text-green-600"
                    title={_('Unblock')}
                  >
                    <FontAwesomeIcon icon="lock-open" />
                  </button>
                ) : status === 'revoked' ? (
                  <button
                    onClick={() => handleReactivate(inv.hash)}
                    className="icon-button text-blue-600"
                    title={_('Reactivate')}
                  >
                    <FontAwesomeIcon icon="undo" />
                  </button>
                ) : (
                  <span className="text-muted text-xs">{_('No actions')}</span>
                )}

                  {session.role === 'leader' &&
                    !isLeader &&
                    effectiveGroupInfo?.state !== 'closed' && (
                    <button
                      onClick={() => setDeleteTarget(inv.hash)}
                      className="icon-button text-red-500"
                      title={_('Delete')}
                    >
                      <FontAwesomeIcon icon="trash" />
                    </button>
                  )}
              </div>
            </div>
          )
        })}
      </div>

      {/* Controles de paginación */}
      <PaginationControls />

      {showEditor && groupInfo && (
        <GroupInfoEditor
          groupInfo={groupInfo}
          onClose={() => setShowEditor(false)}
          onSave={handleSaveGroupInfo}
        />
      )}

      {showCreator && (
        <InvitationCreator
          onClose={() => setShowCreator(false)}
          onSave={(newInvite, meta = {}) => {
            setInvitations(prev => [...prev, newInvite]);

            // muestra mensaje según meta (funciona aunque meta no llegue)
            if (meta.emailSent) {
              flashOk(_('Invitation created and email sent.'));
            } else if (meta.notifyByEmail) {
              flashOk(_('Invitation created; email could not be sent.'));
            } else {
              flashOk(_('Invitation created.'));
            }
          }}
          groupName={groupInfo?.group_name}
          ownerName={groupInfo?.owner_name}
          ownerEmail={groupInfo?.owner_email}
        />
      )}

      {deleteTarget && (
        <InvitationDeleteModal
          hash={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
        />
      )}

      {/* Diálogo simple de reenvío */}
      {resendOpen && resendTarget && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white dark:bg-gray-900 rounded-xl p-4 shadow-xl w-[min(92vw,480px)]">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-base font-semibold">
                {_('Resend invitation email')}
              </h3>
              <button
                className="icon-button text-slate-500"
                onClick={() => setResendOpen(false)}
                title={_('Close')}
              >
                <FontAwesomeIcon icon="xmark" />
              </button>
            </div>

            <p className="text-sm mb-3">
              <span className="font-medium">{_('Recipient')}:</span>{' '}
              {resendTarget.contact_name || '-'} &lt;
              {resendTarget.contact_email || '-'}&gt;
            </p>

            <label className="inline-flex items-center gap-2 mb-4">
              <input
                type="checkbox"
                className="h-4 w-4 accent-indigo-600"
                checked={ccLeader}
                onChange={(e) => setCcLeader(e.target.checked)}
              />
              <span className="text-sm">
                {_('Send a copy to the leader')}
              </span>
            </label>

            {error && (
              <p className="text-sm text-red-600 mb-2">{error}</p>
            )}

            <div className="flex items-center justify-end gap-2">
              <button
                className="button-secondary"
                onClick={() => setResendOpen(false)}
                disabled={resending}
              >
                {_('Cancel')}
              </button>
              <button
                className="button-primary"
                onClick={handleResend}
                disabled={resending}
              >
                {resending ? _('Sending...') : _('Send')}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}
